import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { cn } from '@/lib/utils';
import { handlePaste, type PasteMode } from '@/lib/utils/pasteHandler';
import { processHTMLContent, applyFormatting } from '@/lib/utils/textFormatting';
import { useTextHighlighting } from '@/providers/TextHighlightingProvider';
import { getCursorPosition, restoreCursorPosition } from '@/components/editor/utils/cursorUtils';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TextFieldRegularProps {
  initialValue?: string;
  placeholder?: string;
  className?: string;
  onValueChange?: (value: string) => void;
  readOnly?: boolean;
  minHeight?: string;
  maxHeight?: string;
  /**
   * How to handle paste:
   *  - "plain": paste as plain text (default) to avoid huge HTML payloads from apps like Figma/Docs
   *  - "basic-html": allow a minimal HTML subset via sanitizer
   */
  pasteMode?: PasteMode;
}

interface BadgeElement {
  id: string;
  type: 'badge';
  text: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  color?: string;
}

interface ImageElement {
  id: string;
  type: 'image';
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

interface TextElement {
  id: string;
  type: 'text';
  text: string;
  style: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: string;
    fontSize?: string;
  };
}

type ContentElement = TextElement | BadgeElement | ImageElement;

export default function TextFieldRegular({
  initialValue = '',
  placeholder = 'Start typing...',
  className = '',
  onValueChange,
  readOnly = false,
  minHeight = '100px',
  maxHeight = '400px',
  pasteMode = 'plain'
}: TextFieldRegularProps) {
  const { t } = usePlatformTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const highlightMenuRef = useRef<HTMLDivElement>(null);

  const { highlightItems } = useTextHighlighting();

  // Set initial content
  useEffect(() => {
    if (editorRef.current && initialValue) {
      const processedContent = processHTMLContent(initialValue);
      editorRef.current.innerHTML = processedContent;
    }
  }, [initialValue]);

  // Handle clicking outside highlight menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (highlightMenuRef.current && !highlightMenuRef.current.contains(event.target as Node)) {
        setShowHighlightMenu(false);
      }
    };

    if (showHighlightMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHighlightMenu]);

  // Handle input changes (no longer calls onValueChange immediately)
  const handleInput = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      
  
      // Note: onValueChange is no longer called here - it will be called on blur
    }
  };

  // Handle text selection
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setShowToolbar(false);
      return;
    }
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
        
    if (selectedText && !readOnly) {
      setSelectedText(selectedText);
      setSelectionRange({
        start: range.startOffset,
        end: range.endOffset
      });
      
      // Position toolbar near selection
      const rect = range.getBoundingClientRect();
      setToolbarPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 50
      });
      setShowToolbar(true);
    } else {
      setShowToolbar(false);
    }
  }, [readOnly]);

  // Apply style to selected text
  const applyStyle = (style: Partial<TextElement['style']>) => {
    
    if (!selectedText) {
      console.log('No selected text, returning');
      return;
    }
    
    // Get current selection
    const selection = window.getSelection();
    
    if (!selection || selection.rangeCount === 0) {
      console.log('No valid selection found');
      return;
    }
    
    const range = selection.getRangeAt(0);
    
    // Preserve the selection before any operations
    const preservedRange = range.cloneRange();
    
    // Focus the editor to ensure commands work
    editorRef.current?.focus();
    
    // Restore the preserved selection
    selection.removeAllRanges();
    selection.addRange(preservedRange);
    
    // Apply each style individually using modern API
    if (style.bold) {
      const strong = document.createElement('strong');
      preservedRange.surroundContents(strong);
    }
    
    if (style.italic) {
      const em = document.createElement('em');
      preservedRange.surroundContents(em);
    }
    
    if (style.underline) {
      const u = document.createElement('u');
      preservedRange.surroundContents(u);
    }

    // Clear selection
    selection.removeAllRanges();
    
    // Process the content to ensure proper text flow and prevent layout issues
    if (editorRef.current) {
      // Store cursor position before processing
      const cursorPosition = getCursorPosition(editorRef.current);
      
      const processedContent = processHTMLContent(editorRef.current.innerHTML);
      editorRef.current.innerHTML = processedContent;
      
      // Restore cursor position after processing
      setTimeout(() => {
        if (editorRef.current) {
          restoreCursorPosition(editorRef.current, cursorPosition);
        }
      }, 0);
    }
    
    setShowToolbar(false);
    setSelectedText('');
  };

  // Insert badge
  const insertBadge = (variant: BadgeElement['variant'] = 'default') => {
    const badgeText = prompt('Enter badge text:');
    if (badgeText && editorRef.current) {
      editorRef.current.focus();
      const badgeElement = document.createElement('span');
      badgeElement.className = 'inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10';
      badgeElement.textContent = badgeText;
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(badgeElement);
        range.setStartAfter(badgeElement);
        range.setEndAfter(badgeElement);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Process the content to ensure proper text flow
        if (editorRef.current) {
          const processedContent = processHTMLContent(editorRef.current.innerHTML);
          editorRef.current.innerHTML = processedContent;
        }
      }
    }
  };

  // Insert image
  const insertImage = () => {
    fileInputRef.current?.click();
  };

  // Apply highlighting to selected text
  const applyHighlight = (item: any) => {
    if (!editorRef.current || !selectedText) return;
    
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      // Create highlighted span
      const span = document.createElement('span');
      span.className = 'text-highlight';
      span.setAttribute('data-highlight-id', item.id);
      span.setAttribute('data-highlight-type', item.type);
      span.textContent = selectedText;
      
      // Replace selection with highlighted span
      range.deleteContents();
      range.insertNode(span);
      
      // Update content
      const processedContent = processHTMLContent(editorRef.current.innerHTML);
      editorRef.current.innerHTML = processedContent;
      onValueChange?.(processedContent);
      
      // Hide highlight menu
      setShowHighlightMenu(false);
      setShowToolbar(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editorRef.current) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const src = e.target?.result as string;
        editorRef.current?.focus();
        const imgElement = document.createElement('img');
        imgElement.src = src;
        imgElement.alt = file.name;
        imgElement.className = 'max-w-full h-auto rounded-sm';
        
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.insertNode(imgElement);
          range.setStartAfter(imgElement);
          range.setEndAfter(imgElement);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Process the content to ensure proper text flow
          if (editorRef.current) {
            const processedContent = processHTMLContent(editorRef.current.innerHTML);
            editorRef.current.innerHTML = processedContent;
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle click to start editing
  const handleClick = () => {
    if (!readOnly) {
      setIsEditing(true);
    }
  };

  // Save changes when focus is lost
  const handleBlur = () => {
    setIsEditing(false);
    setShowToolbar(false);
    
    // Save the current content when focus is lost
    if (editorRef.current && onValueChange) {
      // Process the content to ensure proper text flow before saving
      const processedContent = processHTMLContent(editorRef.current.innerHTML);
      onValueChange(processedContent);
    }
  };

  // Handle key press for new line
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Let the contentEditable handle the line break naturally
      document.execCommand('insertLineBreak', false);
    }
  };

  return (
    <div className={cn('relative', className)}>
      {/* Main editor area */}
      <div
        ref={editorRef}
        dir="auto"
        className={cn(
          'w-full p-4 focus:outline-none focus:ring-0',
          'border-t border-b border-gray-200',
          'focus:border-t-blue-500 focus:border-b-blue-500',
          'transition-colors duration-200',
          isEditing && 'border-t-gray-500 border-b-gray-500',
          readOnly && 'bg-gray-50 cursor-default',
          'resize-y overflow-auto',
          'whitespace-pre-wrap break-words'
        )}
        style={{
          minHeight,
          maxHeight,
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
        contentEditable={!readOnly && isEditing}
        suppressContentEditableWarning
        onFocus={handleClick}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        onInput={handleInput}
        onPaste={(e) => handlePaste(e, pasteMode, onValueChange)}
        onMouseUp={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        onClick={handleClick}
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
      >
        {!isEditing && !editorRef.current?.innerHTML && (
          <span className="text-gray-400">{placeholder}</span>
        )}
      </div>

      {/* Floating toolbar */}
      {showToolbar && (
        <div
          ref={toolbarRef}
          className="absolute z-50 bg-white border border-gray-200 rounded-sm shadow-lg p-2 flex items-center gap-1"
          style={{
            left: toolbarPosition.x,
            top: toolbarPosition.y,
            transform: 'translateX(-50%)'
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyStyle({ bold: true });
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="h-8 w-8 p-0"
          >
            <IconProvider iconstring="fa-bold" size={12} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyStyle({ italic: true });
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="h-8 w-8 p-0"
          >
            <IconProvider iconstring="fa-italic" size={12} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyStyle({ underline: true });
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="h-8 w-8 p-0"
          >
            <IconProvider iconstring="fa-underline" size={12} />
          </Button>
          
          <div className="w-px h-6 bg-gray-200 mx-1" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Apply bullet list
              if (editorRef.current) {
                const normalizedContent = applyFormatting('insertUnorderedList', undefined, editorRef.current);
                onValueChange?.(normalizedContent);
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="h-8 w-8 p-0"
            title={t('platform.cards.text-fields.bullet_list')}
          >
            <IconProvider iconstring="fa-list-ul" size={12} />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Apply numbered list
              if (editorRef.current) {
                const normalizedContent = applyFormatting('insertOrderedList', undefined, editorRef.current);
                onValueChange?.(normalizedContent);
              }
            }}
            onMouseDown={(e) => e.preventDefault()}
            className="h-8 w-8 p-0"
            title={t('platform.cards.text-fields.numbered_list')}
          >
            <IconProvider iconstring="fa-list-ol" size={12} />
          </Button>
          
          <div className="w-px h-6 bg-gray-200 mx-1" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => insertBadge('default')}
            className="h-8 px-2 text-xs"
          >
            <IconProvider iconstring="fa-tag" size={10} className="mr-1" />
            Badge
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={insertImage}
            className="h-8 px-2 text-xs"
          >
            <IconProvider iconstring="fa-image" size={10} className="mr-1" />
            Image
          </Button>
          
          <div className="w-px h-6 bg-gray-200 mx-1" />
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHighlightMenu(!showHighlightMenu)}
            className="h-8 px-2 text-xs"
            title={t('platform.cards.text-fields.highlight_text')}
          >
            <IconProvider iconstring="fa-highlighter" size={10} className="mr-1" />
            Highlight
          </Button>
        </div>
      )}

      {/* Highlight menu */}
      {showHighlightMenu && selectedText && (
        <div
          ref={highlightMenuRef}
          className="absolute z-50 bg-white border border-gray-200 rounded-sm shadow-lg p-2 min-w-48"
          style={{
            left: toolbarPosition.x,
            top: toolbarPosition.y + 40,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="text-xs font-medium text-gray-700 mb-2">{t('platform.cards.text-fields.highlight_as')}</div>
          {highlightItems.length === 0 ? (
            <div className="text-xs text-gray-500 p-2">{t('platform.cards.text-fields.no_highlight_items_available')}</div>
          ) : (
            <div className="space-y-1">
              {highlightItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => applyHighlight(item)}
                  className="w-full text-left px-2 py-1 text-xs hover:bg-gray-100 rounded flex items-center justify-between"
                >
                  <span>{item.type}: {item.keys.join(', ')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Quick action buttons when not editing */}
      {!isEditing && !readOnly && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-6 w-6 p-0"
          >
            <IconProvider iconstring="fa-edit" size={10} />
          </Button>
        </div>
      )}
    </div>
  );
}
